// Counts the sign-ins that leave for Google, Microsoft or SSO and come back to
// the app without finishing.
//
// A sign-in that dies on the provider's own pages left nothing behind: no
// callback, no `sign_in_failed`, only the absence of `oauth_callback_received`.
// Over October that absence was about a third of Microsoft clickers, and
// nothing said whether they closed the tab or came back. A marker written just
// before the redirect and read on the next page load in the same tab turns the
// back-button case into an event, with the time spent away. Closing the tab is
// still silent: clicks minus callbacks minus this event is that group.

import {
 OnboardingEvents, trackEvent
} from './posthog'
import {
 CALLBACK_ROUTE, PENDING_ROUTE
} from '../utils/routes'

const SIGN_IN_PROVIDERS = ['google', 'microsoft', 'sso'] as const
export type SignInProvider = typeof SIGN_IN_PROVIDERS[number]

const PENDING_SIGN_IN_KEY = 'catch_pending_sign_in'

// Landings that mean the sign-in finished: the callback page, and the waitlist
// page a signup past the daily cap is sent to instead.
const FINISHED_PATHS = new Set([CALLBACK_ROUTE, PENDING_ROUTE])

interface PendingSignIn {
  provider: SignInProvider
  startedAt: number
  // Settings' reconnect of an existing account, not a front-door sign-in. The
  // backend's refusal log calls the same thing `is_reconnect`.
  reconnect?: boolean
}

function isPendingSignIn(value: unknown): value is PendingSignIn {
  if (typeof value !== 'object' || value === null) return false
  const { provider, startedAt } = value as Record<string, unknown>
  return SIGN_IN_PROVIDERS.includes(provider as SignInProvider) && typeof startedAt === 'number'
}

export function rememberSignInStarted(provider: SignInProvider, reconnect = false): void {
  try {
    const pending: PendingSignIn = { provider, startedAt: Date.now(), reconnect }
    sessionStorage.setItem(PENDING_SIGN_IN_KEY, JSON.stringify(pending))
  } catch {
    // Storage blocked (private mode, quota): the sign-in goes ahead uncounted.
  }
}

/** Every sign-in redirect goes out through here, so every one is counted. */
export function leaveForSignIn(
  provider: SignInProvider,
  authUrl: string,
  { reconnect = false }: { reconnect?: boolean } = {},
): void {
  rememberSignInStarted(provider, reconnect)
  window.location.href = authUrl
}

// Read once: whatever this load concludes, the next one must not count the same
// sign-in again.
function takePendingSignIn(): PendingSignIn | null {
  try {
    const raw = sessionStorage.getItem(PENDING_SIGN_IN_KEY)
    if (raw === null) return null
    sessionStorage.removeItem(PENDING_SIGN_IN_KEY)
    const parsed: unknown = JSON.parse(raw)
    return isPendingSignIn(parsed) ? parsed : null
  } catch {
    return null
  }
}

export function reportUnfinishedSignIn(restoredFromCache = false): void {
  const pending = takePendingSignIn()
  if (pending === null) return
  if (FINISHED_PATHS.has(window.location.pathname)) return
  // The provider sent the person back with a refusal, which the login page
  // already counts as `sign_in_failed` with its reason.
  if (new URLSearchParams(window.location.search).has('error')) return
  trackEvent(OnboardingEvents.SIGN_IN_RETURNED_UNFINISHED, {
    provider: pending.provider,
    is_reconnect: pending.reconnect === true,
    seconds_away: Math.max(0, Math.round((Date.now() - pending.startedAt) / 1000)),
    // Back from the provider often restores the page from the back-forward
    // cache, which runs no startup code; `pageshow` is the only signal then.
    restored_from_cache: restoredFromCache,
  })
}

export function watchForUnfinishedSignIn(): void {
  reportUnfinishedSignIn()
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) reportUnfinishedSignIn(true)
  })
}
